import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, Bot, User as UserIcon, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logIA } from "@/lib/iaLogger";

const SUGGESTIONS = {
  founder: [
    "Quels sont les principaux risques cette semaine ?",
    "Quelle classe a le plus d'impayés ?",
    "Quels élèves sont en baisse de performance ?",
    "Quelles recommandations pour améliorer le recouvrement ?",
  ],
  finance: [
    "Qui sont les parents à relancer en priorité ?",
    "Rédige un message de relance courtois en français.",
    "Quelles sont les anomalies de paiement à signaler ?",
    "Quel est le total des impayés ce mois-ci ?",
  ],
  admin: [
    "Quels dossiers d'inscription sont incomplets ?",
    "Rédige une réponse type pour confirmer une inscription.",
    "Génère un communiqué pour la prochaine réunion parents.",
    "Quelles statistiques d'inscription pour le trimestre ?",
  ],
  teacher: [
    "Identifie les élèves en difficulté de ma classe.",
    "Propose un plan de soutien pour les élèves faibles en maths.",
    "Aide-moi à rédiger une appréciation positive.",
    "Suggère 5 exercices de révision pour CE2 en français.",
  ],
  parent: [
    "Comment lire le bulletin de mon enfant ?",
    "Quels frais sont dus pour ce trimestre ?",
    "Comment contacter l'enseignant principal ?",
    "Mon enfant a une difficulté, que faire ?",
  ],
  general: [
    "Quels sont les cycles proposés par les Écoles Mazid ?",
    "Comment inscrire mon enfant ?",
    "Quels documents sont nécessaires pour l'inscription ?",
    "Quels sont les horaires de l'école ?",
  ],
};

export default function AIPage() {
  const { user } = useAuth();
  const context = SUGGESTIONS[user?.role] ? user.role : "general";
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Bonjour ${user?.full_name || ""} ! Je suis **MAZID AI**, votre assistant intelligent propulsé par Claude. Comment puis-je vous aider aujourd'hui ?` },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(`page-${Date.now()}`);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    logIA("chat_message", { resource_title: msg.slice(0, 100) });

    try {
      const token = localStorage.getItem("mazid_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current, context }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const d = line.slice(6);
          if (d === "[DONE]") continue;
          setMessages((m) => {
            const updated = [...m];
            updated[updated.length - 1] = { role: "assistant", content: updated[updated.length - 1].content + d };
            return updated;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const u = [...m];
        u[u.length - 1] = { role: "assistant", content: "Erreur de connexion à l'IA. Veuillez réessayer." };
        return u;
      });
    } finally { setStreaming(false); }
  };

  return (
    <div className="space-y-4 h-[calc(100vh-180px)] flex flex-col" data-testid="page-ai">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Sparkles className="h-3 w-3" /><span>Assistant intelligent · Claude Sonnet 4.5</span></div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">MAZID AI Assistant</h2>
        </div>
      </div>

      <div className="flex-1 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-page-msg-${i}`}>
              <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-[#1E3A8A] text-white" : "bg-[#EA580C] text-white"}`}>
                {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              </div>
              <div className={`max-w-[75%] px-5 py-3 rounded-2xl ${m.role === "user" ? "bg-[#1E3A8A] text-white rounded-tr-sm" : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"}`}>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content || (streaming && i === messages.length - 1 ? "..." : "")}</div>
              </div>
            </div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-white">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Suggestions</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {(SUGGESTIONS[context] || SUGGESTIONS.general).map((s, i) => (
                <button key={i} onClick={() => send(s)} className="text-left text-sm p-3 rounded-lg border border-slate-200 hover:border-[#EA580C] hover:bg-orange-50 transition" data-testid={`ai-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-slate-200 p-4 bg-white flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Posez votre question à MAZID AI..."
            rows={2}
            className="flex-1 resize-none border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#166534]/30 focus:border-[#166534]"
            data-testid="ai-page-input"
          />
          <button onClick={() => send()} disabled={streaming || !input.trim()} className="px-5 rounded-lg bg-[#EA580C] text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 font-semibold" data-testid="ai-page-send">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Envoyer</>}
          </button>
        </div>
      </div>
    </div>
  );
}
